import { type AreaId, type DocumentKind } from '@/james-playground/aa-family-claim/claim/asks';
import { type Value } from '@/james-playground/aa-family-claim/claim/facts';

/**
 * The people the prototype can be started as, one per pathway (spec 1). Every
 * name, address and number is made up. Each scenario carries what KareHero
 * already knows (profile and account), the scripted voice notes the orb plays,
 * and what each document photo reads, standing in for live extraction.
 *
 * Proposal values follow the registry's conventions: option labels for
 * choices, arrays for multi-choice, rows keyed by column id for tables.
 */

export type ScenarioId = 'standard' | 'endOfLife' | 'careHome' | 'herself' | 'attorney';

export type Proposal = { id: string; value: Value; quote?: string };

export type ScriptLine = {
  text: string;
  /** Seconds into the recording, for the clock. */
  at: number;
  /** Hub areas this line answers, lit as it is heard. */
  lights: AreaId[];
  proposals: Proposal[];
};

export type Recording = { seconds: number; lines: ScriptLine[] };

export type DocumentFixture = { file: string; proposals: Proposal[] };

export type Scenario = {
  id: ScenarioId;
  label: string;
  /** One line for the scenario switch. */
  summary: string;
  selfClaim: boolean;
  claimant: { first: string; full: string };
  /** The person using the app, when it is not the claimant. */
  helper?: { first: string; full: string; relationship: string };
  profile: Proposal[];
  account: Proposal[];
  callNotes?: { date: string; proposals: Proposal[] };
  /** Keyed by voice prompt id: the opening note, then second passes. */
  voice: Partial<Record<string, Recording>>;
  documents: Partial<Record<DocumentKind, DocumentFixture>>;
  bank: { holder: string; bank: string };
};

const line = (
  at: number,
  text: string,
  lights: AreaId[],
  proposals: Proposal[],
): ScriptLine => ({
  at,
  text,
  lights,
  // Every proposal from a line carries the line as its quote.
  proposals: proposals.map((p) => ({ ...p, quote: p.quote ?? text })),
});

const yes = (id: string, quote?: string): Proposal => ({ id, value: 'Yes', quote });
const set = (id: string, value: Value, quote?: string): Proposal => ({ id, value, quote });

// ---- Margaret, at home: the standard journey -----------------------------

const MARGARET: Scenario = {
  id: 'standard',
  label: 'At home',
  summary: 'Sarah claims for her mum, Margaret, who lives at home in Bristol.',
  selfClaim: false,
  claimant: { first: 'Margaret', full: 'Margaret Cole' },
  helper: { first: 'Sarah', full: 'Sarah Cole', relationship: 'Daughter' },
  profile: [
    set('surname', 'Cole'),
    set('firstNames', 'Margaret'),
    set('title', 'Mrs'),
    set('dob', '19/03/1942'),
    set('sex', 'Female'),
    set('address', '12 Example Road\nBristol'),
    set('postcode', 'BS1 1AB'),
    set('phone', '0117 000 0000'),
    set('nationality', 'British'),
    set('accommodation', 'House'),
    set('existingDisabilityAward', 'None of these'),
    // The care profile says she sleeps through: the voice note will disagree.
    set('nightNeedsHelp', 'No', 'Care profile, 22 September: sleeps through, no help at night.'),
  ],
  account: [
    set('hasHelper', 'Yes'),
    set('helpersTable', [
      {
        helperName: 'Sarah Cole',
        helperRelationship: 'Daughter',
        helperAddress: '30 Example Avenue\nBristol',
        helperPostcode: 'BS2 2AB',
        helperPhone: '07700 900000',
      },
    ]),
  ],
  callNotes: {
    date: '12 September',
    proposals: [
      set('currentStay', 'No', 'Call 12 September: Margaret is at home, no recent hospital stays.'),
      set('previousStayLastSixWeeks', 'No', 'Call 12 September: Margaret is at home, no recent hospital stays.'),
      set('constantAttendance', ['Neither (both boxes left blank)'], 'Call 12 September: no war pension or industrial injuries benefits.'),
      set('normallyLiveGb', 'Yes', 'Call 12 September: has always lived in Bristol.'),
      set('abroadOverFourWeeks', 'No', 'Call 12 September: has not been abroad in years.'),
    ],
  },
  voice: {
    'voice-opening': {
      seconds: 52,
      lines: [
        line(0, 'Mum needs me every morning. I help her out of bed because her knees are so stiff,', ['day'], [
          yes('bedNeedsHelp'),
          yes('bedDifficultyGettingOut'),
          yes('bedHelpGettingOut'),
        ]),
        line(6, 'then into the shower, and I wash her lower legs and feet.', ['day'], [
          yes('washingNeedsHelp'),
          set('showeringHelpFrequency', '1'),
          set('washingHelpFrequency', '1'),
        ]),
        line(10, 'I do her socks and trousers, because she can’t bend down.', ['day'], [
          yes('dressingNeedsHelp'),
          set('dressingHelpFrequency', '1'),
        ]),
        line(14, 'She uses a frame indoors, and I stay beside her on the stairs and the way to the bathroom.', ['day', 'home'], [
          yes('movingIndoorsNeedsHelp'),
          yes('indoorStairsHelp'),
          yes('indoorWalkingDifficulty'),
          set('aidsTable', [{ aidName: 'Walking frame', aidBenefit: 'Helps her walk around the house' }]),
        ]),
        line(20, 'It’s been like this since she fell in March last year.', ['day'], [
          set('careStartDate', 'March 2025'),
          set('fallsOrStumbles', ['Fall']),
          set('lastFall', 'March 2025'),
        ]),
        line(24, 'I cut up her food at lunch and tea, she manages after that.', ['day'], [
          yes('eatingNeedsHelp'),
          set('cuttingFoodHelpFrequency', '2'),
        ]),
        line(28, 'I remind her to take her tablets morning and night, and check she’s taken them.', ['day'], [
          yes('medicationNeedsHelp'),
          set('medicationPromptFrequency', '2'),
        ]),
        line(32, 'Most nights she calls out for help to get to the toilet, usually twice, and it takes me about fifteen minutes to settle her.', ['night'], [
          yes('nightNeedsHelp'),
          set('nightToiletFrequency', '2'),
          set('nightToiletMinutes', '15'),
          set('nightCareNightsPerWeek', '6'),
        ]),
        line(41, 'I’m round there every day.', ['day', 'helpers'], [
          set('dayCareDaysPerWeek', '7'),
        ]),
        line(44, 'Some days the stiffness lasts longer, maybe two days a week, and getting dressed takes much longer.', ['day'], [
          set('variabilityDay', 'Worse on some days'),
          set('variabilityDayOften', 'about two days a week, when the stiffness lasts longer'),
        ]),
      ],
    },
    'voice-safe': {
      seconds: 9,
      lines: [
        line(0, 'She’s fine on her own for a few hours, her mind is sharp, it’s her knees.', ['safe'], [
          set('daySupervisionNeeded', 'No'),
          set('nightSupervisionNeeded', 'No'),
        ]),
      ],
    },
    'voice-home': {
      seconds: 10,
      lines: [
        line(0, 'It’s a two-storey house, the bathroom and her bedroom are both upstairs.', ['home'], [
          set('accommodation', 'House'),
          set('toiletLocation', ['Upstairs']),
          set('sleepLocation', ['Upstairs']),
        ]),
      ],
    },
    'voice-health': {
      seconds: 11,
      lines: [
        line(0, 'She sees the rheumatology clinic at the hospital every six months, last time in July.', ['health'], [
          yes('seenProfessional'),
          set('professionalsTable', [
            {
              professionalRole: 'Rheumatology clinic',
              professionalFrequency: 'Every six months',
              professionalLastSeen: 'July 2026',
              professionalConditions: 'Osteoarthritis',
            },
          ]),
        ]),
      ],
    },
  },
  documents: {
    prescription: {
      file: 'Prescription, 10 September',
      proposals: [
        set('illnessesTable', [
          { conditions: 'Osteoarthritis in both knees', medications: 'Paracetamol 500 mg tablets', medicationDosage: 'Two tablets up to four times a day' },
          { conditions: 'High blood pressure', medications: 'Amlodipine 5 mg tablets', medicationDosage: 'One tablet each morning' },
        ], 'Prescription issued 10 September'),
        set('gpName', 'Example Medical Practice', 'Prescription issued 10 September'),
        set('gpAddress', '1 Example Street\nBristol', 'Prescription issued 10 September'),
        set('gpPostcode', 'BS1 1AB', 'Prescription issued 10 September'),
      ],
    },
    hospital_letter: {
      file: 'Clinic letter, 14 July',
      proposals: [
        yes('seenProfessional', 'Rheumatology clinic letter, 14 July'),
        set('professionalsTable', [
          {
            professionalName: 'Dr A Example',
            professionalRole: 'Consultant rheumatologist',
            professionalAddress: 'Example Hospital\nBristol',
            professionalPostcode: 'BS10 5AA',
            professionalConditions: 'Osteoarthritis in both knees',
            professionalFrequency: 'Every six months',
            professionalLastSeen: '14/07/2026',
          },
        ], 'Rheumatology clinic letter, 14 July'),
        set('surgeryWaitingList', 'No', 'Rheumatology clinic letter, 14 July'),
      ],
    },
  },
  bank: { holder: 'Margaret Cole', bank: 'Example Bank' },
};

// ---- Patricia, end of life ------------------------------------------------

const PATRICIA: Scenario = {
  id: 'endOfLife',
  label: 'End of life',
  summary: 'Daniel claims for his mum, Patricia, in Cardiff. Her consultant has said it may be months.',
  selfClaim: false,
  claimant: { first: 'Patricia', full: 'Patricia Hughes' },
  helper: { first: 'Daniel', full: 'Daniel Hughes', relationship: 'Son' },
  profile: [
    set('surname', 'Hughes'),
    set('firstNames', 'Patricia'),
    set('title', 'Mrs'),
    set('dob', '02/11/1946'),
    set('sex', 'Female'),
    set('address', '4 Example Close\nCardiff'),
    set('postcode', 'CF10 1AA'),
    set('nationality', 'British'),
    set('existingDisabilityAward', 'None of these'),
  ],
  account: [
    set('hasHelper', 'Yes'),
    set('helpersTable', [
      { helperName: 'Daniel Hughes', helperRelationship: 'Son', helperPhone: '07700 900001' },
    ]),
  ],
  voice: {
    'voice-opening': {
      seconds: 34,
      lines: [
        line(0, 'Mum was diagnosed with lung cancer in the spring and it has spread.', ['health'], [
          set('illnessesTable', [{ conditions: 'Lung cancer', conditionsDuration: 'Since April 2026' }]),
        ]),
        line(6, 'The consultant at the cancer centre told us in August it is likely months.', ['health'], [
          yes('seenProfessional'),
          set('professionalsTable', [
            { professionalRole: 'Consultant oncologist', professionalConditions: 'Lung cancer', professionalLastSeen: 'August 2026' },
          ]),
        ]),
        line(13, 'She’s very breathless now. I stay with her most nights and her neighbour pops in at lunchtime.', ['helpers'], [
          set('helpersTable', [
            { helperName: 'Daniel Hughes', helperRelationship: 'Son', helperSupport: 'Stays most nights, helps with washing and dressing', helperFrequency: 'Most days and nights' },
            { helperName: 'A neighbour', helperRelationship: 'Neighbour', helperSupport: 'Checks on her at lunchtime', helperFrequency: 'Most days' },
          ]),
        ]),
        line(21, 'The district nurses come twice a week.', ['health'], []),
      ],
    },
  },
  documents: {
    prescription: {
      file: 'Prescription, 22 September',
      proposals: [
        set('illnessesTable', [
          { conditions: 'Lung cancer', conditionsDuration: 'Since April 2026', medications: 'Morphine sulfate oral solution', medicationDosage: 'As directed for breathlessness and pain' },
        ], 'Prescription issued 22 September'),
        set('gpName', 'Example Surgery', 'Prescription issued 22 September'),
        set('gpAddress', '9 Example Street\nCardiff', 'Prescription issued 22 September'),
        set('gpPostcode', 'CF11 1AA', 'Prescription issued 22 September'),
      ],
    },
  },
  bank: { holder: 'Patricia Hughes', bank: 'Example Bank' },
};

// ---- Joan, in a care home --------------------------------------------------

const JOAN: Scenario = {
  id: 'careHome',
  label: 'Care home',
  summary: 'Helen claims for her mum, Joan, who moved into a care home in August. The council pays part.',
  selfClaim: false,
  claimant: { first: 'Joan', full: 'Joan Price' },
  helper: { first: 'Helen', full: 'Helen Price', relationship: 'Daughter' },
  profile: [
    set('surname', 'Price'),
    set('firstNames', 'Joan'),
    set('dob', '14/06/1938'),
    set('sex', 'Female'),
    set('address', '7 Example Terrace\nBath'),
    set('postcode', 'BA2 1AA'),
    set('nationality', 'British'),
    set('existingDisabilityAward', 'None of these'),
  ],
  account: [],
  voice: {
    'voice-opening': {
      seconds: 41,
      lines: [
        line(0, 'Mum has been in Example House care home since the start of August, after a fall at home.', ['stays', 'day'], [
          yes('currentStay'),
          set('currentStayPlaceType', 'Care home'),
          set('currentStayFrom', '03/08/2026'),
          set('currentStayName', 'Example House Care Home'),
          set('fallsOrStumbles', ['Fall']),
          set('lastFall', 'July 2026'),
        ]),
        line(7, 'The council pays part of it and we pay the rest from her savings.', ['stays'], [
          yes('publiclyFundedStay'),
          set('fundingAuthority', 'Example Council'),
        ]),
        line(12, 'She has vascular dementia. She gets confused and tries to go out, so the staff keep an eye on her all day.', ['safe'], [
          yes('daySupervisionNeeded'),
          yes('supervisionReasonConfusion'),
          yes('supervisionReasonWandering'),
          yes('supervisionReasonUnawareOfDanger'),
          set('safeAloneDuration', 'Only a few minutes'),
          set('daySupervisionDaysPerWeek', '7'),
        ]),
        line(20, 'At night she gets up confused, and the night staff check on her three or four times.', ['night'], [
          yes('nightSupervisionNeeded'),
          yes('nightWatchReasonConfusion'),
          yes('nightWatchReasonWandering'),
          set('nightSupervisionTimes', '3'),
          set('nightSupervisionDuration', 'About 20 minutes each time'),
          set('nightSupervisionNightsPerWeek', '7'),
        ]),
        line(27, 'They help her wash and dress every morning, and take her to the toilet.', ['day'], [
          yes('washingNeedsHelp'),
          set('washingHelpFrequency', '1'),
          yes('dressingNeedsHelp'),
          set('dressingHelpFrequency', '2'),
          yes('toiletNeedsHelp'),
          set('toiletHelpFrequency', '6'),
        ]),
        line(33, 'Someone has to sit with her at meals or she forgets to eat.', ['day'], [
          yes('eatingNeedsHelp'),
          set('eatingPromptFrequency', '3'),
          set('dayCareDaysPerWeek', '7'),
        ]),
      ],
    },
  },
  documents: {
    care_plan: {
      file: 'Care plan, Example House',
      proposals: [
        set('illnessesTable', [{ conditions: 'Vascular dementia', conditionsDuration: 'Diagnosed 2024' }], 'Care plan, Example House'),
        set('currentStayAddress', 'Example House\n2 Example Lane', 'Care plan, Example House'),
        set('currentStayTown', 'Bath', 'Care plan, Example House'),
        set('currentStayPostcode', 'BA1 1AA', 'Care plan, Example House'),
      ],
    },
  },
  bank: { holder: 'Joan Price', bank: 'Example Bank' },
};

// ---- Rose, claiming for herself --------------------------------------------

const ROSE: Scenario = {
  id: 'herself',
  label: 'She claims herself',
  summary: 'Rose, in Leeds, fills in her own claim.',
  selfClaim: true,
  claimant: { first: 'Rose', full: 'Rose Bennett' },
  profile: [
    set('surname', 'Bennett'),
    set('firstNames', 'Rose'),
    set('dob', '08/01/1945'),
    set('sex', 'Female'),
    set('address', '21 Example Gardens\nLeeds'),
    set('postcode', 'LS1 1AA'),
    set('mobile', '07700 900002'),
    set('nationality', 'British'),
    set('existingDisabilityAward', 'None of these'),
    set('signingForSomeoneElse', 'No'),
  ],
  account: [],
  voice: {
    'voice-opening': {
      seconds: 38,
      lines: [
        line(0, 'I’ve got macular degeneration, so I can’t see well, and arthritis in my hands.', ['health'], [
          set('illnessesTable', [
            { conditions: 'Macular degeneration', conditionsDuration: 'About five years' },
            { conditions: 'Arthritis in both hands', conditionsDuration: 'About ten years' },
          ]),
        ]),
        line(7, 'I can’t read my post or the labels on my medicines, so my neighbour Jean reads them for me.', ['day', 'helpers'], [
          yes('communicationNeedsHelp'),
          yes('commDifficultyPost'),
          yes('commHelpPost'),
          yes('medicationNeedsHelp'),
          set('medicationDifficultyFrequency', '2'),
          yes('hasHelper'),
          set('helpersTable', [
            { helperName: 'Jean', helperRelationship: 'Neighbour', helperSupport: 'Reads my post and medicine labels', helperFrequency: 'Most days' },
          ]),
        ]),
        line(15, 'I struggle to cut up food and do up buttons.', ['day'], [
          yes('eatingNeedsHelp'),
          set('cuttingFoodDifficultyFrequency', '2'),
          yes('dressingNeedsHelp'),
          set('dressingDifficultyFrequency', '1'),
        ]),
        line(20, 'I’ve fallen twice this year on the stairs, the last time in August.', ['day'], [
          set('fallsOrStumbles', ['Fall']),
          set('fallsLastYear', '2'),
          set('lastFall', 'August 2026'),
          yes('movingIndoorsNeedsHelp'),
          yes('indoorStairsDifficulty'),
        ]),
      ],
    },
  },
  documents: {
    prescription: {
      file: 'Prescription, 1 September',
      proposals: [
        set('illnessesTable', [
          { conditions: 'Macular degeneration', conditionsDuration: 'About five years', medications: 'Eye injections at the clinic', medicationDosage: 'Every eight weeks' },
          { conditions: 'Arthritis in both hands', conditionsDuration: 'About ten years', medications: 'Ibuprofen gel', medicationDosage: 'Three times a day' },
        ], 'Prescription issued 1 September'),
        set('gpName', 'Example Health Centre', 'Prescription issued 1 September'),
        set('gpAddress', '5 Example Road\nLeeds', 'Prescription issued 1 September'),
        set('gpPostcode', 'LS2 1AA', 'Prescription issued 1 September'),
      ],
    },
  },
  bank: { holder: 'Rose Bennett', bank: 'Example Bank' },
};

// ---- Eileen, with an attorney signing ---------------------------------------

const EILEEN: Scenario = {
  id: 'attorney',
  label: 'Attorney signs',
  summary: 'Mark holds Power of Attorney for his mum Eileen’s money and signs her claim.',
  selfClaim: false,
  claimant: { first: 'Eileen', full: 'Eileen Ward' },
  helper: { first: 'Mark', full: 'Mark Ward', relationship: 'Son' },
  profile: [
    set('surname', 'Ward'),
    set('firstNames', 'Eileen'),
    set('dob', '25/09/1940'),
    set('sex', 'Female'),
    set('address', '3 Example Mews\nNorwich'),
    set('postcode', 'NR1 1AA'),
    set('nationality', 'British'),
    set('existingDisabilityAward', 'None of these'),
  ],
  account: [
    set('representativeName', 'Mark Ward'),
    set('representativeDob', '11/05/1968'),
    set('representativeAddress', '18 Example Way\nNorwich'),
    set('representativePostcode', 'NR2 2AA'),
    set('representativePhone', '07700 900003'),
  ],
  voice: {
    'voice-opening': {
      seconds: 44,
      lines: [
        line(0, 'Mum has Alzheimer’s. She can’t be left for more than an hour, she leaves the gas on.', ['safe', 'health'], [
          set('illnessesTable', [{ conditions: 'Alzheimer’s disease', conditionsDuration: 'Since 2023' }]),
          yes('daySupervisionNeeded'),
          yes('supervisionReasonUnawareOfDanger'),
          yes('supervisionReasonConfusion'),
          set('safeAloneDuration', 'About an hour'),
          set('daySupervisionDaysPerWeek', '7'),
        ]),
        line(8, 'She has gone out at night twice, so I sleep over and get up when she wanders.', ['night'], [
          yes('nightSupervisionNeeded'),
          yes('nightWatchReasonWandering'),
          yes('nightWatchReasonConfusion'),
          set('nightSupervisionTimes', '2'),
          set('nightSupervisionDuration', 'About 30 minutes each time'),
          set('nightSupervisionNightsPerWeek', '7'),
        ]),
        line(16, 'She needs reminding to wash, dress and eat, or she just won’t.', ['day'], [
          yes('washingNeedsHelp'),
          set('washingPromptFrequency', '1'),
          yes('dressingNeedsHelp'),
          set('dressingPromptFrequency', '1'),
          yes('eatingNeedsHelp'),
          set('eatingPromptFrequency', '3'),
          set('dayCareDaysPerWeek', '7'),
        ]),
        line(23, 'I’ve got power of attorney for her money, it’s registered.', ['signing'], [
          set('signingForSomeoneElse', 'Yes'),
          set('signerCategory', 'I hold Power of Attorney for Property and Finance'),
        ]),
      ],
    },
  },
  documents: {
    prescription: {
      file: 'Prescription, 15 September',
      proposals: [
        set('illnessesTable', [
          { conditions: 'Alzheimer’s disease', conditionsDuration: 'Since 2023', medications: 'Donepezil 10 mg tablets', medicationDosage: 'One tablet each night' },
        ], 'Prescription issued 15 September'),
        set('gpName', 'Example Practice', 'Prescription issued 15 September'),
        set('gpAddress', '2 Example Square\nNorwich', 'Prescription issued 15 September'),
        set('gpPostcode', 'NR3 1AA', 'Prescription issued 15 September'),
      ],
    },
  },
  bank: { holder: 'Eileen Ward', bank: 'Example Bank' },
};

export const SCENARIOS: Scenario[] = [MARGARET, PATRICIA, JOAN, ROSE, EILEEN];

export const SCENARIO: Record<ScenarioId, Scenario> = Object.fromEntries(
  SCENARIOS.map((s) => [s.id, s]),
) as Record<ScenarioId, Scenario>;

/** Everything a recording proposes, in the order it was said. */
export const proposalsOf = (recording: Recording): Proposal[] =>
  recording.lines.flatMap((l) => l.proposals);

export const transcriptOf = (recording: Recording): string =>
  recording.lines.map((l) => l.text).join(' ');
